import Immutable from 'immutable';
import { createNameGenerator } from './nameGenerator';
import intForDay from './intForDay';

// either resolves a key by a string value or a lookup function. Handles immutable.
export const resolveKeyValue = (obj, keyOrResolver, isImmutable = false) => {
  let value;
  if (typeof keyOrResolver === 'function') {
    value = keyOrResolver(obj);
  } else {
    value = isImmutable ? obj.get(keyOrResolver) : obj[keyOrResolver];
  }
  return value;
};

export const mapToNameField = (generatedName, type) => {
  switch (type) {
    case 'first_name':
      return generatedName.firstName;
    case 'last_name':
      return generatedName.lastName;
    case 'full_name':
      return generatedName.fullName;
    default:
      throw Error('Unsupported anonymizer field type.');
  }
};

export const hashInject = (src, inject) => {
  let hash = (src << 5) - src + inject;
  hash |= 0;
  return hash;
};

export const generateName = (sourceObject, seedResolver, isImmutable) => {
  const seedSrc = resolveKeyValue(sourceObject, seedResolver, isImmutable);
  const dayInt = intForDay();
  let seed = hashInject(seedSrc, dayInt); // shitty but ok entropy changing by day
  const nameGenerator = createNameGenerator({ seed });
  return nameGenerator();
};

/**
  Build a mapper function to anonymize an object or map over an iterable of them.
  Configure the anonymizer according to the shape and content of the object to be anonymized.

  Full example:

  const schemaFields = [
    {
      key: 'first_name',
      type: 'first_name',
    },
    {
      key: 'last_name',
      type: 'last_name',
    },
    {
      key: 'display_name',
      type: 'full_name',
    },
  ];

  const profiles = [
    { user_id: 1, first_name: 'Matt', last_name: 'Fisher', display_name: 'Matt Fisher' },
    { user_id: 2, first_name: 'Kai', last_name: 'Hirsinger', display_name: 'Kai Hirsinger' },
    { user_id: 3, first_name: 'Danny', last_name: 'Shaw', display_name: 'Danny Shaw' },
    { user_id: 4, first_name: 'Daniel', last_name: 'Carr', display_name: 'Daniel Carr' },
  ];

  const profileAnonymizer = buildAnonymizer({
    seedResolver: 'user_id',  // or p => p.get('user_id')
    schemaFields: fieldResovlers,
  })

  const anonymousProfiles = profiles.map(profileAnonymizer);

**/
export default ({ seedResolver = 'id', schemaFields = [] }) => {
  return (sourceObject) => {
    const isImmutable = Immutable.Iterable.isIterable(sourceObject);
    const generatedName = generateName(sourceObject, seedResolver, isImmutable);

    // don't mutate plain js objects.
    let anonymizedObject = isImmutable ? sourceObject : { ...sourceObject };
    schemaFields.forEach((field) => {
      const val = mapToNameField(generatedName, field.type);
      if (isImmutable) {
        anonymizedObject = anonymizedObject.set(field.key, val);
      } else {
        anonymizedObject[field.key] = val;
      }
    });

    return anonymizedObject;
  };
};
